import type { paths } from '../api-types';
import { supabase } from './supabase';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Primitive = string | number | boolean | undefined | null;

let getToken = async (): Promise<string | null> => {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
};

/** Test seam only. Production code never calls this. */
export function __setTokenGetterForTests(fn: () => Promise<string | null>) {
  getToken = fn;
}

export async function apiGet<P extends keyof paths & string>(
  path: P,
  params?: Record<string, Primitive | readonly string[]>,
): Promise<unknown> {
  const token = await getToken();
  if (!token) throw new ApiError(401, 'unauthorized', 'No active session');

  // Same-origin: Cloudflare proxies /api/* to the Fly origin in production, and
  // Vite's dev proxy does the same locally. A relative URL is therefore correct in
  // both, and there is no VITE_API_BASE_URL to get wrong per environment.
  const API_PREFIX = '/api';
  const url = new URL(`${API_PREFIX}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const v of value) url.searchParams.append(key, v);
    } else {
      url.searchParams.set(key, String(value));
    }
  }

  const res = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
  });

  if (res.ok) return res.json();

  // The API's documented contract is uniformly sendError's { error: { code, message } }
  // (ASC-606 unified every error response on this shape, including app.authenticate's
  // 401 — see apps/api/src/lib/httpErrors.ts and apps/api/src/middleware/auth.ts). The
  // flat `{ error: 'Unauthorized' }` string shape is NOT part of that contract any more.
  // The string-body branch below is kept purely as defensive tolerance — a proxy,
  // gateway, or future regression could still hand back a non-conforming body, and
  // degrading safely to a sensible code/message beats throwing on JSON.parse or losing
  // the status entirely.
  let code = 'unknown';
  let message = res.statusText;
  try {
    const body = (await res.json()) as { error?: string | { code?: string; message?: string } };
    if (typeof body.error === 'string') {
      // Non-conforming body (e.g. a flat error string). Not the documented contract —
      // still map a 401 to 'unauthorized' so callers get a sensible code regardless.
      code = res.status === 401 ? 'unauthorized' : 'error';
      message = body.error;
    } else if (body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
    }
  } catch {
    // Non-JSON error body — keep the status text.
  }
  throw new ApiError(res.status, code, message);
}
